import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { Message } from '../../models/message';
import { Transport } from '../../models/transport';
import { AError } from '../../models/aerror';

import { ToolbarService } from '../../services/toolbar.service';
import { EventService } from '../../services/event.service';
import { LocationService } from '../../services/location.service';
import { LoggingService } from '../../services/logging.service';
import { MessageService } from '../../services/message.service';
import { ScannerService } from '../../services/scanner.service';
import { TourService } from '../../services/tour.service';
import { ModalService } from '../../services/modal.service';
import { BeepService } from '../../services/beep.service';
import { AppUrls } from '../../app.urls';
import { LocationAwareComponent } from '../location-aware/location-aware.component';

@Component({
  selector: 'app-transport-details',
  templateUrl: './transport-details.component.html',
  styleUrls: ['./transport-details.component.scss']
})
export class TransportDetailsComponent extends LocationAwareComponent implements OnInit, OnDestroy {

  // set class on host element
  @HostBinding('class') classes = 'app-content';

  transport: Transport;

  didScanStartCode = false;
  private canLeave = false;

  private guideMessage: Message;
  private successMessage: Message;

  private scanResultSubscription: Subscription;
  private settingsButtonSubscription: Subscription;

  constructor(
    private router: Router,
    private location: Location,
    private events: EventService,
    private logger: LoggingService,
    private messageService: MessageService,
    private changeDetectorRef: ChangeDetectorRef,
    private scanner: ScannerService,
    private beep: BeepService,
    private tourService: TourService,
    private translate: TranslateService,
    private modal: ModalService,
    public toolbar: ToolbarService
  ) {
    super();
    this.transport = tourService.currentTransport;
    this.didScanStartCode = !tourService.needsTransportStartCode;
    if (!this.transport) {
      console.error(`No transport passed`);
      this.router.navigate([AppUrls.ROOT, AppUrls.Home], { replaceUrl: true });
      throw Error('No transport passed!');
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.canLeave = false;
    this.toolbar.setToolbarConfig({
      backAction: () => this.onBack(),
      showRefresh: true,
      showSettings: true,
      showCall: true,
      showLogout: false
    });
    this.logger.log('Showing transport details', this.transport);

    if (!this.didScanStartCode) {
      this.showErrorMessage(this.translate.instant('transportDetails.scanStartCode'));
    } else {
      this.showGuideMessage(this.translate.instant('transportDetails.scanItems'));
    }

    this.scanResultSubscription = this.scanner.scanResult.subscribe(scanResult => {
      this.scanner.pauseScanning();
      this.didScan(scanResult.data);
    });
    this.settingsButtonSubscription = this.events.settingsButtonClicked.subscribe(() => {
      this.canLeave = true;
    });

    this.fetchStatus();
  }

  override ngOnDestroy(): void {
    this.settingsButtonSubscription.unsubscribe();
    this.scanResultSubscription.unsubscribe();
    if (this.guideMessage) {
      this.messageService.remove(this.guideMessage);
    }
    if (this.successMessage) {
      this.messageService.remove(this.successMessage);
    }
    this.messageService.removeMultiple(this.messageService.warnings);
    super.ngOnDestroy();
  }

  private onBack(): void {
    if (this.canLeave || !this.didStart) {
      this.tourService.currentTransport = undefined;
      this.location.back();
      return;
    }

    this.messageService.add(Message.warning(this.translate.instant('transportDetails.leavePrompt')), 2000);
  }

  // ===== View helpers ===== //

  get didStart(): boolean {
    const didNotStart = (!this.didScanStartCode
      || (this.transport.pickupItemStatus.scanned < 1 && this.transport.handoverItemStatus.scanned < 1));
    return !didNotStart;
  }

  openAddress(): void {
    this.locationService.showDirectionsTo(this.transport.address);
  }

  back(): void {
    this.onBack();
  }

  done(): void {
    if (this.transport.hasScannedAll) {
      this.finishTransport();
    } else {
      this.scanner.pauseScanning();
      this.modal.showConfirmationModal(
        undefined,
        this.translate.instant('transportDetails.notAllScannedPrompt'),
        this.translate.instant('ok'),
        this.translate.instant('cancel')
      ).then(result => {
        if (result) {
          this.finishTransport();
        }
      }).finally(() => {
        this.scanner.resumeScanning();
      });
    }
  }

  // ===== Scanner delegate ===== //

  didScan(barcode: string): void {
    if (!this.didScanStartCode) {
      this.tryToStartTransport(barcode)
        .then(isSuccessful => isSuccessful ? this.beep.beep() : this.beep.bop());
    } else {
      this.tryToScanTransportItem(barcode)
        .then(isSuccessful => isSuccessful ? this.beep.beep() : this.beep.bop());
    }
  }

  // ===== Transport (order/tour stop) operations ===== //

  private tryToStartTransport(barcode: string): Promise<boolean> {
    return this.tourService.startTransport(barcode)
      .then(transport => {
        if (!transport.matches(this.transport)) {
          return Promise.reject(AError.wrongTransportScanned);
        }

        this.transport = transport;
        this.didScanStartCode = true;
        this.showGuideMessage(this.translate.instant('transportDetails.scanItems'));
        this.showSuccessMessage(this.translate.instant('transportDetails.started'), 2000);
        this.changeDetectorRef.detectChanges();
        this.scanner.resumeScanning();
        return Promise.resolve(true);
      })
      .catch(someError => {
        const error = AError.fromSomeError(someError);
        if (error.is(AError.wrongTransportScanned)) {
          this.showErrorMessage(this.translate.instant('transportDetails.wrongTransportScanned'));
        } else {
          this.showErrorMessage(error.humanMessage);
        }
        this.scanner.resumeScanning();
        return Promise.resolve(false);
      });
  }

  private tryToScanTransportItem(barcode: string): Promise<boolean> {
    return this.tourService.scanTransportItem(barcode, this.transport)
      .then(transport => {
        this.transport = transport;
        this.showSuccessMessage(this.translate.instant('transportDetails.scannedItem', { barcode }));
        this.changeDetectorRef.detectChanges();
        this.scanner.resumeScanning();
        return true;
      })
      .catch(someError => {
        this.showErrorMessage(AError.fromSomeError(someError).humanMessage);
        this.scanner.resumeScanning();
        this.changeDetectorRef.detectChanges();
        return Promise.resolve(false);
      });
  }

  private finishTransport(): void {
    this.tourService.finishTransport(this.transport)
      .then(() => {
        this.tourService.currentTransport = undefined;
        this.canLeave = true;
        this.onBack();
      })
      .then(() => this.messageService.add(Message.success(this.translate.instant('transportDetails.finished')), 2000))
      .catch(error => {
        this.showErrorMessage(AError.fromSomeError(error).humanMessage);
      });
  }

  private fetchStatus(): void {
    this.tourService.getTransportStatus(this.transport)
      .then(transport => this.transport = transport)
      .catch(error => {
        this.showErrorMessage(AError.fromSomeError(error).humanMessage);
      });
  }

  // ===== Transport plan ===== //

  openTransportPlan(): void {
    this.router.navigate([AppUrls.ROOT, AppUrls.TransportPlan]);
  }

  // ===== Message helpers ===== //

  private showGuideMessage(text: string): void {
    if (this.guideMessage) {
      this.messageService.remove(this.guideMessage);
      this.messageService.removeMultiple(this.messageService.warnings);
    }

    this.guideMessage = Message.info(text);
    this.messageService.add(this.guideMessage);
  }

  private showSuccessMessage(text: string, removeAfter?: number): void {
    if (this.successMessage) {
      this.messageService.remove(this.successMessage);
      this.messageService.removeMultiple(this.messageService.warnings);
    }

    this.successMessage = Message.success(text);
    if (removeAfter) {
      this.messageService.add(this.successMessage, removeAfter);
    } else {
      this.messageService.add(this.successMessage);
    }
  }

  private showErrorMessage(text: string): void {
    if (this.successMessage) {
      this.messageService.remove(this.successMessage);
    }
    this.messageService.add(Message.warning(text));
  }
}
